import React from 'react'
import { AssessmentCard } from './assessment-card'
import { getStyles } from '../../utils'
import DataAssets from './data-assets'
import { DimensionDescription, Metadata } from '../../types'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { ScoreCardsUpdate } from './scorecards-update'
import { T } from '@tolgee/react'

type AssessmentDetailsProps = {
  totalIssues: number
  issuePercentage: number
  scoreCards: {
    title: string
    score?: number
    dimension_description?: DimensionDescription
    total?: number
    passed?: number
  }[]
  metaData?: Metadata | {}
  isLoading?: boolean
  view?: 'metaData' | 'dataQuality'
  error?: Error | null
}

export function AssessmentDetails({
  totalIssues,
  issuePercentage = 0,
  scoreCards,
  metaData = {},
  isLoading,
  view,
  error,
}: AssessmentDetailsProps) {
  const [pageIndex, setPageIndex] = React.useState(0)
  const cardsPerPage = 3

  const start = pageIndex * cardsPerPage
  const end = start + cardsPerPage
  const visibleCards = scoreCards.slice(start, end)

  const handleNext = () => {
    if (end < scoreCards.length) setPageIndex((prev) => prev + 1)
  }

  const handlePrev = () => {
    if (start > 0) setPageIndex((prev) => prev - 1)
  }

  if (isLoading) {
    return (
      <>
        <div className='flex h-[198px] w-full gap-4'>
          <div className='w-3/4'>
            <Skeleton className='h-full' />
          </div>
          <div className='w-1/4'>
            <Skeleton className='h-full' />
          </div>
        </div>
      </>
    )
  }

  if (error) {
    return (
      <div className='text-center text-error-300'>
        <T keyName='shared.error.system' />
      </div>
    )
  }

  return (
    <div className='w-full rounded'>
      <div className='flex flex-wrap items-start gap-4'>
        <div className='flex flex-1 flex-col gap-1'>
          <div className='flex flex-1 items-center gap-4'>
            <div
              className={`flex h-[144px] min-w-0 flex-1 flex-col justify-center rounded-lg ${getStyles(issuePercentage).bg} p-2 pl-4 pr-4`}
            >
              <p className={`truncate text-sm font-medium text-neutral-white`}>Overall Score</p>
              <div className='my-5'>
                <p className={`text-lg font-bold text-neutral-white`}>
                  {view === 'metaData'
                    ? issuePercentage
                      ? parseFloat(issuePercentage.toFixed())
                      : 0
                    : view === 'dataQuality'
                      ? issuePercentage
                        ? parseFloat(issuePercentage.toFixed())
                        : 'NA'
                      : parseFloat(issuePercentage.toFixed())}
                  <span className='text-[9px] text-neutral-white'>%</span>
                </p>
                <Progress
                  value={issuePercentage}
                  className='mt-1 h-1.5 bg-opacity-50'
                  color='bg-neutral-white'
                />
                {view === 'dataQuality' && (
                  <p className='my-1 text-xs text-neutral-white'>Issues: {totalIssues}</p>
                )}
              </div>
            </div>
            {visibleCards.map((assessment) => (
              <AssessmentCard
                title={assessment.title}
                key={assessment.title}
                score={assessment.score || 0}
                total={assessment.total}
                passed={assessment.passed}
                description={assessment.dimension_description?.description || ''}
                view={view}
              />
            ))}
          </div>
          {scoreCards.length > 3 && (
            <div className='flex items-center justify-end gap-2'>
              <div className='col-span-full flex items-center justify-center gap-4'>
                <Button
                  className='border-input bg-background hover:bg-accent hover:text-accent-foreground h-6 w-6 border-gray-300 shadow-sm'
                  onClick={handlePrev}
                  unstyled
                >
                  <ChevronLeft />
                </Button>
                <span className='text-xs text-gray-600'>
                  {pageIndex + 1}/{scoreCards.length / cardsPerPage}
                </span>
                <Button
                  className='border-input bg-background hover:bg-accent hover:text-accent-foreground h-6 w-6 border-gray-300 shadow-sm'
                  onClick={handleNext}
                  unstyled
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>
        <DataAssets metaData={metaData} />
      </div>
    </div>
  )
}
